/**
 * Image Helpers - Utilities for optimized image loading
 */

/**
 * Generate responsive srcset for an image
 * @param {string} baseUrl - Base image URL
 * @param {Object} sizes - Object with size names and widths
 * @returns {string} - srcset attribute value
 */
export const generateSrcSet = (baseUrl, sizes = {}) => {
  if (!baseUrl) return '';
  
  // If the image is from Cloudinary, use their transformation API
  if (baseUrl.includes('cloudinary.com')) {
    return generateCloudinarySrcSet(baseUrl, sizes);
  }
  
  // For local images, assume they follow the responsive naming convention
  const srcSetEntries = Object.entries(sizes).map(([name, width]) => {
    // Replace extension with responsive version
    const responsiveUrl = baseUrl.replace(/(\.[^.]+)$/, `_${name}$1`);
    return `${responsiveUrl} ${width}w`;
  });
  
  return srcSetEntries.join(', ');
};

/**
 * Generate Cloudinary srcset with transformations
 */
const generateCloudinarySrcSet = (baseUrl, sizes) => {
  const srcSetEntries = Object.entries(sizes).map(([name, width]) => {
    // Insert transformation parameters into Cloudinary URL
    const transformedUrl = baseUrl.replace(
      '/upload/',
      `/upload/w_${width},q_auto,f_auto/`
    );
    return `${transformedUrl} ${width}w`;
  });
  
  return srcSetEntries.join(', ');
};

/**
 * Get optimal image size for current viewport
 * @returns {string} - Size descriptor
 */
export const getOptimalImageSize = () => {
  const width = window.innerWidth;
  
  if (width <= 320) return 'small';
  if (width <= 640) return 'medium';
  return 'large';
};

/**
 * Check if browser supports WebP
 * @returns {Promise<boolean>}
 */
export const supportsWebP = async () => {
  if (typeof document === 'undefined') return true;
  
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

/**
 * Check if browser supports lazy loading
 * @returns {boolean}
 */
export const supportsLazyLoading = () => {
  return 'loading' in HTMLImageElement.prototype;
};

/**
 * Get image dimensions maintaining aspect ratio
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @param {number} maxWidth
 * @param {number} maxHeight
 * @returns {Object} - New dimensions
 */
export const getScaledDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  const aspectRatio = originalWidth / originalHeight;
  
  let width = originalWidth;
  let height = originalHeight;
  
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }
  
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return {
    width: Math.round(width),
    height: Math.round(height)
  };
};

/**
 * Create a blur hash placeholder (simplified version)
 * @param {string} color - Base color for placeholder
 * @returns {string} - Data URL for placeholder
 */
export const createPlaceholder = (color = '#e0e0e0') => {
  // Create a 1x1 pixel SVG with the given color
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1">
    <rect width="100%" height="100%" fill="${color}"/>
  </svg>`;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Preload critical images
 * @param {Array<string>} urls - Image URLs to preload
 */
export const preloadImages = (urls) => {
  urls.forEach(url => {
    const img = new Image();
    img.src = url;
  });
};

/**
 * Get image format from URL
 * @param {string} url
 * @returns {string}
 */
export const getImageFormat = (url) => {
  if (!url) return 'unknown';
  
  const extension = url.split('.').pop().toLowerCase();
  
  const formatMap = {
    'webp': 'WebP',
    'jpg': 'JPEG',
    'jpeg': 'JPEG',
    'png': 'PNG',
    'gif': 'GIF',
    'avif': 'AVIF',
    'heic': 'HEIC'
  };
  
  return formatMap[extension] || extension.toUpperCase();
};

/**
 * Calculate image loading priority
 * @param {string} position - Position on page (above-fold, below-fold)
 * @param {boolean} isLCP - Is Largest Contentful Paint element
 * @returns {string} - loading priority
 */
export const getLoadingPriority = (position = 'below-fold', isLCP = false) => {
  if (isLCP || position === 'above-fold') {
    return 'high';
  }
  return 'auto';
};

/**
 * Fix media URL to ensure proper formatting
 * @param {string} url
 * @returns {string|null}
 */
export const fixMediaUrl = (url) => {
  if (!url) return null;
  
  // If already a full URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If starts with /storage/, ensure it's properly formatted
  if (url.startsWith('/storage/')) {
    return url;
  }
  
  // If starts with storage/ without leading slash, add it
  if (url.startsWith('storage/')) {
    return '/' + url;
  }
  
  // Default: assume it's a relative path that needs /storage/ prefix
  return '/storage/' + url;
};

/**
 * Default responsive sizes configuration
 */
export const DEFAULT_RESPONSIVE_SIZES = {
  small: 320,
  medium: 640,
  large: 1080
};

/**
 * Default aspect ratios for common image types
 */
export const ASPECT_RATIOS = {
  profile: 1, // 1:1
  cover: 3,   // 3:1
  story: 0.5625, // 9:16 (portrait)
  loveshot: 1, // 1:1
  message: 1  // 1:1
};
